'use client';
import { FC } from 'react';

import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { BadgeIllustration } from 'src/general/components/BadgeIllustration/BadgeIllustration';
import { BADGE } from 'src/general/components/BadgeIllustration/BadgeIllustration.type';
import { ProductDetailBadge } from 'src/types/ProductDetail.type';

import { getMicrocopy } from 'src/stores/microcopyStore';
import styles from './Car360V1.module.scss';
import { CAR_OVERVIEW } from './Car360V1.types';
import classNames from 'classnames';

type Car360V1Props = {
  items: ProductDetailBadge[];
};

type ContentBlockProps = {
  [key in CAR_OVERVIEW]: {
    microcopy: MICROCOPY;
    icon: BADGE;
  };
};

const getPlural = (reference: any) => (Number(reference) > 1 ? 's' : '');

const contentBlock: ContentBlockProps = {
  [CAR_OVERVIEW.regoRemaining]: {
    microcopy: MICROCOPY.PDP_CAR_OVERVIEW_REGO,
    icon: BADGE.REGO,
  },
  [CAR_OVERVIEW.lowKmPerYear]: {
    microcopy: MICROCOPY.PDP_CAR_OVERVIEW_LOW_KM_YEAR,
    icon: BADGE.LOW_KMS_PER_YEAR,
  },
  [CAR_OVERVIEW.lowKm]: {
    microcopy: MICROCOPY.PDP_CAR_OVERVIEW_LOW_KM,
    icon: BADGE.LOW_KMS,
  },
  [CAR_OVERVIEW.recentlyServiced]: {
    microcopy: MICROCOPY.PDP_CAR_OVERVIEW_LAST_SERVICED,
    icon: BADGE.SERVICE,
  },
  [CAR_OVERVIEW.singleOwner]: {
    microcopy: MICROCOPY.PDP_CAR_OVERVIEW_SINGLE_OWNER,
    icon: BADGE.SINGLE_OWNER,
  },
  [CAR_OVERVIEW.fullSpareTyre]: {
    microcopy: MICROCOPY.PDP_CAR_OVERVIEW_FULL_SPARE_TYRE,
    icon: BADGE.SPARE_TYRE,
  },
  [CAR_OVERVIEW.twoNewTyres]: {
    microcopy: MICROCOPY.PDP_CAR_OVERVIEW_TWO_NEW_TYRES,
    icon: BADGE.TWO_TYRES,
  },
  [CAR_OVERVIEW.fourNewTyres]: {
    microcopy: MICROCOPY.PDP_CAR_OVERVIEW_FOUR_NEW_TYRES,
    icon: BADGE.FOUR_TYRES,
  },
};

export const Car360V1: FC<Car360V1Props> = ({ items }) => {
  // remove un-declared badges from items to prevent error
  const safeItems = items.filter((item) => item.name in contentBlock);

  return (
    <div className={styles.root}>
      {safeItems.length !== 0 && (
        <div className={styles.layout}>
          {safeItems.map((item) => (
            <Car360V1Item key={item.name} badge={item} />
          ))}
        </div>
      )}
    </div>
  );
};

const Car360V1Item: FC<{ badge: ProductDetailBadge }> = ({ badge }) => {
  const content = getMicrocopy(contentBlock[badge.name].microcopy);
  const contentWithReference = getMicrocopy(contentBlock[badge.name].microcopy, true, {
    reference: badge.reference?.toString() ?? '',
    plural: getPlural(badge.reference),
  });

  return (
    <div key={badge.name} className={styles.info}>
      <BadgeIllustration
        shape="circle"
        background="gradient"
        icon={contentBlock[badge.name].icon}
        className={styles.badge}
      />
      <div
        className={classNames(styles.description, 'c-fw-bold', 'c-fs-body2')}
        data-testid={`${contentBlock[badge.name].microcopy}`}
      >
        {badge.reference ? contentWithReference : content}
      </div>
    </div>
  );
};
